import React from 'react'
import { formatBigMoney, formatMoney, formatPercentage } from '../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { lowercaseFirstLetter } from '../../utils/stringConversion'
import { getPreviousSubscriptionPeriods, getSubscriptionRate } from '../warrants/WarrantOutcome'
import { NewsItem } from 'types/NewsItem'

interface NewsHeadlineOutcomeProps {
    locale: string
    row: NewsItem
}

const getNewsHeadline = (news: NewsItem, locale: string, t: TFunction, typeTranslated?: string) => {
    const { companyName, currency, eventType, issueData, warrantData } = news
    if (issueData) {
        const { unitSubTotal, unitSubPrice, unitPctSubGuarantor, unitSubGuarantor, type } = issueData
        const secondaryOffering = issueData.secondaryOffering !== null ? issueData.secondaryOffering : 0
        const secondarySub = issueData.secondarySub !== null ? issueData.secondarySub : 0
        const totalOfferedUnits = (issueData.offeredUnits ?? 0) + (issueData.offeredUnitsUnlisted ?? 0)

        const subRate = formatPercentage(
            (100 * ((unitSubTotal ?? 0) + secondarySub)) / (totalOfferedUnits + secondaryOffering),
            {
                locale,
                decimals: 0,
            },
        )
        const guarantorsPart =
            (100 * (unitSubGuarantor || 0)) / (unitSubTotal ?? 0) +
            ((unitPctSubGuarantor || 0) * (totalOfferedUnits || 0)) / (unitSubTotal ?? 0)

        if (type === 'Rights issue') {
            const baseKey =
                eventType === 'preliminary outcome'
                    ? 'pages.news.headlines.outcome.preliminary'
                    : 'pages.news.headlines.outcome.final'
            let headline = t(baseKey, { companyName, rate: subRate })

            if (guarantorsPart) {
                headline += t('pages.news.headlines.outcome.guarantorShare', {
                    share: formatPercentage(guarantorsPart, {
                        locale,
                        decimals: 0,
                    }),
                })
            }

            return headline
        } else if (type === 'ABB Directed issue') {
            return t('pages.news.headlines.outcome.abbDirected', {
                companyName,
                issueType: typeTranslated,
                price: formatMoney(unitSubPrice || 0, {
                    locale,
                    currencySymbol: currency,
                }),
                amount: formatBigMoney(+(unitSubPrice || 0) * (unitSubTotal ?? 0), {
                    locale,
                    currencySymbol: currency,
                }),
            })
        } else if (type === 'IPO') {
            return t('pages.news.headlines.outcome.ipo', { companyName, rate: subRate })
        }
    } else if (warrantData) {
        const subscriptionPeriods = getPreviousSubscriptionPeriods(warrantData)
        const sortedSubscriptionPeriods = subscriptionPeriods.sort(
            (a, b) => new Date(b.subEndDate).getTime() - new Date(a.subEndDate).getTime(),
        )
        if (sortedSubscriptionPeriods.length > 0 && sortedSubscriptionPeriods[0].sharesSubscribed !== null) {
            return t('pages.news.headlines.outcome.warrant', {
                companyName,
                name: news.name || warrantData.name,
                rate: formatPercentage(getSubscriptionRate(warrantData, sortedSubscriptionPeriods[0]), {
                    locale,
                    decimals: 0,
                }),
            })
        }
    }
    return ''
}

function NewsHeadlineOutcome({ row, locale }: NewsHeadlineOutcomeProps) {
    const { t } = useTranslation()

    const typeTranslated = row.issueData
        ? lowercaseFirstLetter(t(`entities.issueType.values.${row.issueData.type}`))
        : undefined

    const newsHeadline = getNewsHeadline(row, locale, t, typeTranslated)

    return <span>{newsHeadline}</span>
}

export default NewsHeadlineOutcome
